import type { Crop } from '@protector/entities/crop/models';
import { ProductType } from '@protector/utils/constants';
import { PRODUCT_PAGE_SIZE } from 'config/constants';
import { ofType, type ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';
import {
  CropActionsTypes,
  LoadCropFailure,
  LoadCropSuccess,
  LoadCropVarietiesPaginatedFailure,
  LoadCropVarietiesPaginatedSuccess,
  LoadCropsByIdsFailure,
  LoadCropsByIdsSuccess,
  type LoadCrop,
  type LoadCropVarieties,
  type LoadCropsByIds
} from './crop.actions';
import { getAllVarietiesPaginated, getCrop, getCropsByIds, getVarietiesByCompanyIdPaged } from './crop.service';

export const handleLoadCrop = (action$: ActionsObservable<ReturnType<typeof LoadCrop>>) =>
  action$.pipe(
    ofType(CropActionsTypes.LOAD_CROP),
    concatMap(() =>
      getCrop().pipe(
        map(response => response.data),
        map(cropPage => {
          return LoadCropSuccess(cropPage);
        }),
        catchError((error: string) => of(LoadCropFailure(error)))
      )
    )
  );

export const handleLoadCropVarieties = (action$: ActionsObservable<ReturnType<typeof LoadCropVarieties>>) =>
  action$.pipe(
    ofType(CropActionsTypes.LOAD_CROP_VARIETIES),
    map(action => action.payload),
    concatMap(request => {
      const { cropId, companyId } = request;

      return getVarietiesByCompanyIdPaged(companyId, cropId, ProductType.VARIETY, 0, PRODUCT_PAGE_SIZE).pipe(
        concatMap(firstPage => {
          if (firstPage?.last || firstPage?.totalPages === 0) {
            const parsedCrop: Crop = {
              id: cropId,
              company_id: companyId,
              varieties: firstPage.content
            };
            return of(LoadCropVarietiesPaginatedSuccess([parsedCrop]));
          }

          return getAllVarietiesPaginated(
            companyId,
            cropId,
            firstPage?.totalPages ?? firstPage.total_pages,
            ProductType.VARIETY,
            PRODUCT_PAGE_SIZE,
            true
          ).pipe(
            map(response => {
              const parsedCrop: Crop = {
                id: cropId,
                company_id: companyId,
                varieties: firstPage.content.concat(response.flatMap(item => item.content))
              };

              return LoadCropVarietiesPaginatedSuccess([parsedCrop]);
            }),
            catchError((error: string) => of(LoadCropVarietiesPaginatedFailure(error)))
          );
        })
      );
    })
  );

export const handleLoadCropByIds = (action$: ActionsObservable<ReturnType<typeof LoadCropsByIds>>) =>
  action$.pipe(
    ofType(CropActionsTypes.LOAD_CROPS_BY_IDS),
    map(action => action.payload),
    concatMap(cropIDs =>
      getCropsByIds(cropIDs).pipe(
        map(response => {
          return LoadCropsByIdsSuccess(response);
        })
      )
    ),
    catchError((err: string) => of(LoadCropsByIdsFailure(err)))
  );
